import { useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { createAuth0SorTokenProvider, sorOrganization } from '../hooks/useSorApi';
import {
  clearSorToken,
  getSorAccessToken,
  isSorActiveInSettings,
  setSorEnabled,
  setSorTokenManaged,
  setSorTokenProvider,
  type SorTenantSettingRow,
} from './sorTokenStore';

/**
 * Gets the app-wide SOR token once the tenant settings (GET /api/v2/settings, loaded after the
 * app's own token) have the SOR ability active, and keeps it renewed until unmount. While this
 * is mounted, SOR screens wait for that token instead of fetching their own, and get no token
 * when SOR is off.
 * - doc-web: settings from `useGetGlobalSettingsQuery` (doc-data)
 * - PAL: the settings it already loads, or call setSorToken / setSorTokenProvider directly
 */
export function SorTokenSync({
  settings,
  settingsFailed = false,
}: Readonly<{
  settings?: ReadonlyArray<SorTenantSettingRow> | null;
  /** The settings request failed: screens then fetch the token themselves, as without this component */
  settingsFailed?: boolean;
}>): null {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const organization = sorOrganization(user);
  const sorActive = settings ? isSorActiveInSettings(settings) : null;

  // A tenant switch or logout must not keep the previous tenant's token
  useEffect(() => clearSorToken, [organization, isAuthenticated]);

  useEffect(() => {
    const managed = isAuthenticated && !settingsFailed;
    setSorTokenManaged(managed);
    if (!managed) setSorEnabled(null);
    return () => setSorTokenManaged(false);
  }, [isAuthenticated, settingsFailed]);

  const provider = useMemo(
    () => (isAuthenticated ? createAuth0SorTokenProvider({ user, getAccessTokenSilently }) : null),
    [isAuthenticated, user, getAccessTokenSilently]
  );

  useEffect(() => {
    // Settings not loaded yet: SOR screens wait for them
    if (sorActive === null || !provider) return undefined;
    setSorTokenProvider(provider);
    setSorEnabled(sorActive);
    if (!sorActive) return () => setSorTokenProvider(null);
    // Errors here (e.g. consent still needed) surface again when a SOR screen asks for the token
    getSorAccessToken().catch(() => undefined);
    return () => setSorTokenProvider(null);
  }, [sorActive, provider]);

  return null;
}
